// src/pages/HotelForm.js
// Public sign-up ("/register"): create a property and its owner account in three steps.
// (The export name stays HotelForm because the DB-driven menu and routes refer to it.)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiCheck } from "react-icons/fi";

import axiosInstance from "../api/axiosInstance";
import useAppTheme from "../hooks/useAppTheme";
import Stepper from "./register/Stepper";
import PropertyTypeStep from "./register/PropertyTypeStep";
import DetailsStep from "./register/DetailsStep";
import OwnerStep from "./register/OwnerStep";
import { isEmail, isPhone, isWebsite } from "./register/validators";

import "./cx.css"; // theme tokens (--accent ...)
import "./auth.css";

const STEPS = [
  { key: "type", label: "Property" },
  { key: "details", label: "Details" },
  { key: "owner", label: "Account" },
];

const STEP_FIELDS = {
  1: ["Type"],
  2: ["HotelName", "Email", "ContactNumber", "Address", "Website", "Country", "Province", "District", "TownOrVillage", "PhysicalLocation"],
  3: ["firstName", "lastName", "email", "password", "confirmPassword", "agree"],
};

const DRAFT_KEY = "nnetego-register-draft-v2"; // saved so a refresh doesn't lose what was typed (never passwords)

const EMPTY_HOTEL = {
  HotelName: "",
  Address: "",
  ContactNumber: "",
  CountryDialCode: "+267",
  PhysicalLocation: "",
  TownOrVillage: "",
  District: "",
  Province: "",
  Country: "Botswana",
  Email: "",
  Website: "",
  SocialMediaHandle: "",
  MapCoordinates: "",
  NumberOfRooms: 0,
  Type: "",
  Amenities: [], // set up after sign-up, in the app
  Activities: [],
  Description: "",
  Events: [],
};
const EMPTY_OWNER = { firstName: "", lastName: "", email: "", password: "", confirmPassword: "", agree: false };

const loadDraft = () => {
  try {
    const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
    if (!d) return null;
    return { step: Math.min(Math.max(Number(d.step) || 1, 1), 3), hotel: { ...EMPTY_HOTEL, ...(d.hotel || {}) }, owner: { ...EMPTY_OWNER, ...(d.owner || {}), password: "", confirmPassword: "", agree: false } };
  } catch {
    return null;
  }
};

const normalizeWebsite = (w) => {
  if (!w || !w.trim()) return null;
  const s = w.trim();
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
};

const validate = (h, o) => {
  const e = {};
  if (!h.Type) e.Type = "Choose a property type to continue";

  if (!h.HotelName.trim()) e.HotelName = "Enter the property name";
  if (!h.Email.trim()) e.Email = "Enter a business email";
  else if (!isEmail(h.Email)) e.Email = "That email doesn't look right";
  if (!h.ContactNumber.trim()) e.ContactNumber = "Enter a phone number";
  else if (!isPhone(h.CountryDialCode, h.ContactNumber)) e.ContactNumber = "That number isn't valid for the selected country code";
  if (!h.Address.trim()) e.Address = "Enter the postal address";
  if (!isWebsite(h.Website)) e.Website = "Enter a valid web address, or leave it empty";
  if (!h.Country.trim()) e.Country = "Select the country";
  if (!h.Province.trim()) e.Province = "Enter the province or region";
  if (!h.District.trim()) e.District = "Enter the district";
  if (!h.TownOrVillage.trim()) e.TownOrVillage = "Enter the town or village";
  if (!h.PhysicalLocation.trim()) e.PhysicalLocation = "Enter the plot number or location";

  if (!o.firstName.trim()) e.firstName = "Enter your first name";
  if (!o.lastName.trim()) e.lastName = "Enter your last name";
  if (!o.email.trim()) e.email = "Enter your email";
  else if (!isEmail(o.email)) e.email = "That email doesn't look right";
  if (!o.password) e.password = "Choose a password";
  else if (o.password.length < 8) e.password = "Use at least 8 characters";
  if (o.confirmPassword !== o.password) e.confirmPassword = "The passwords don't match";
  if (!o.agree) e.agree = "Please accept the Terms and Privacy Policy";
  return e;
};

const lowerFirst = (s) => s.charAt(0).toLowerCase() + s.slice(1);

export default function HotelForm() {
  useAppTheme(); // applies the saved brown / green theme
  const navigate = useNavigate();
  const draft = useMemo(loadDraft, []);

  const [step, setStep] = useState(draft?.step || 1);
  const [hotel, setHotel] = useState(draft?.hotel || EMPTY_HOTEL);
  const [owner, setOwner] = useState(draft?.owner || EMPTY_OWNER);
  const [touched, setTouched] = useState({});
  const [attempted, setAttempted] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const topRef = useRef(null);

  useEffect(() => {
    document.title = "Nnetego | Create your account";
  }, []);

  // save progress (without passwords) as the user goes
  useEffect(() => {
    if (done) return;
    try {
      const { password, confirmPassword, agree, ...safeOwner } = owner;
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ step, hotel, owner: safeOwner }));
    } catch {
      /* storage unavailable */
    }
  }, [step, hotel, owner, done]);

  const all = useMemo(() => validate(hotel, owner), [hotel, owner]);
  const stepFields = STEP_FIELDS[step];
  const stepHasErrors = stepFields.some((f) => all[f]);

  // an error shows once the field was left, or after Continue was pressed; server errors always show
  const errors = useMemo(() => {
    const out = {};
    Object.keys(all).forEach((k) => {
      if (touched[k] || attempted) out[k] = all[k];
    });
    return { ...out, ...serverErrors };
  }, [all, touched, attempted, serverErrors]);

  const patchHotel = (p) => {
    setHotel((h) => ({ ...h, ...p }));
    setServerErrors((s) => {
      const n = { ...s };
      Object.keys(p).forEach((k) => delete n[k]);
      return n;
    });
  };
  const patchOwner = (p) => {
    setOwner((o) => ({ ...o, ...p }));
    setServerErrors((s) => {
      const n = { ...s };
      Object.keys(p).forEach((k) => delete n[k]);
      return n;
    });
  };
  const blur = (name) => setTouched((t) => ({ ...t, [name]: true }));

  const goTo = (n) => {
    setStep(n);
    setAttempted(false);
    setFormError("");
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const focusFirstError = () => {
    const first = stepFields.find((f) => all[f]);
    if (!first) return;
    const el = document.getElementById(`f-${first}`) || document.querySelector(`[aria-label="Property type"] button`);
    el?.focus?.();
  };

  const next = () => {
    if (stepHasErrors) {
      setAttempted(true);
      focusFirstError();
      return;
    }
    goTo(step + 1);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (stepHasErrors) {
      setAttempted(true);
      focusFirstError();
      return;
    }

    try {
      setSubmitting(true);
      setFormError("");

      const hotelPayload = {
        ...hotel,
        Website: normalizeWebsite(hotel.Website),
        NumberOfRooms: hotel.NumberOfRooms == null || hotel.NumberOfRooms === "" ? null : Number(hotel.NumberOfRooms),
      };
      if (hotelPayload.Website == null) delete hotelPayload.Website;

      const email = owner.email.trim();
      const res = await axiosInstance.post("/User/RegisterWithHotel", {
        hotel: hotelPayload,
        user: { firstName: owner.firstName.trim(), lastName: owner.lastName.trim(), email, username: email, password: owner.password, role: "Owner" },
      });

      if (res.status === 201 || res.status === 200) {
        try {
          localStorage.removeItem(DRAFT_KEY);
        } catch {
          /* ignore */
        }
        setDone(true);
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.errors && typeof data.errors === "object") {
        const mapped = {};
        Object.keys(data.errors).forEach((key) => {
          const msg = Array.isArray(data.errors[key]) ? data.errors[key][0] : String(data.errors[key]);
          if (key.startsWith("User.")) mapped[lowerFirst(key.slice(5))] = msg;
          else mapped[key.replace(/^Hotel\./, "")] = msg;
        });
        setServerErrors(mapped);
        setFormError("Please fix the highlighted fields.");
        const hotelKeys = STEP_FIELDS[2];
        if (Object.keys(mapped).some((k) => hotelKeys.includes(k))) goTo(2);
      } else if (err.response?.status === 409) {
        setServerErrors({ email: data?.message || "An account with this email already exists." });
        setFormError("");
      } else {
        setFormError(data?.message || (typeof data === "string" ? data : "") || "We couldn't create your account. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-split">
      <aside className="auth-aside">
        <Link to="/" className="auth-aside-logo" aria-label="Nnetego home">
          <img src="/images/logo.png" alt="Nnetego" />
        </Link>

        <div className="auth-aside-body">
          <span className="auth-pill">Hotel Management Solution</span>
          <h1>
            Attention to detail in <em>property management</em>
          </h1>
          <p>Run front desk, housekeeping, POS, and mobile orders from a single, calm dashboard, without spreadsheets or clashing systems.</p>
          <ul className="auth-points">
            <li>
              <FiCheck /> No long contracts
            </li>
            <li>
              <FiCheck /> Go live instantly
            </li>
            <li>
              <FiCheck /> Local support
            </li>
          </ul>
        </div>

        <Link to="/" className="auth-aside-back">
          <FiArrowLeft /> Back to home
        </Link>
      </aside>

      <main className="auth-main" ref={topRef}>
        <div className="auth-main-inner">
          <header className="auth-main-head">
            <Link to="/" className="auth-mobile-logo" aria-label="Nnetego home">
              <img src="/images/logo.png" alt="Nnetego" />
            </Link>
            <p className="auth-switch">
              Already have an account? <Link to="/login">Sign in</Link>
            </p>
          </header>

          {done ? (
            <div className="reg-done" role="status">
              <span className="reg-done-icon">
                <FiCheck />
              </span>
              <h2 className="reg-title">Check your email</h2>
              <p className="reg-sub">
                We sent a confirmation link to <strong>{owner.email}</strong>. Open it to activate your account, then sign in.
              </p>
              <div className="auth-actions center">
                <button type="button" className="auth-btn" onClick={() => navigate("/login", { state: { email: owner.email } })}>
                  Go to sign in
                </button>
              </div>
              <p className="auth-hint center">
                Didn't get it? <Link to="/resend-confirmation">Resend the email</Link>
              </p>
            </div>
          ) : (
            <form onSubmit={submit} noValidate>
              <Stepper steps={STEPS} current={step} onSelect={goTo} />

              <div className="reg-panel" key={step}>
                {step === 1 && <PropertyTypeStep value={hotel.Type} onChange={(t) => patchHotel({ Type: t })} error={errors.Type} />}
                {step === 2 && <DetailsStep data={hotel} patch={patchHotel} errors={errors} onBlur={blur} />}
                {step === 3 && <OwnerStep owner={owner} patch={patchOwner} errors={errors} onBlur={blur} />}
              </div>

              {formError && (
                <div className="auth-alert is-error" role="alert">
                  {formError}
                </div>
              )}

              <div className="auth-actions">
                {step > 1 ? (
                  <button type="button" className="auth-btn auth-btn--ghost" onClick={() => goTo(step - 1)} disabled={submitting}>
                    <FiArrowLeft /> Back
                  </button>
                ) : (
                  <span />
                )}

                {step < 3 ? (
                  <button type="button" className="auth-btn" onClick={next} disabled={step === 1 && !hotel.Type}>
                    Continue
                  </button>
                ) : (
                  <button type="submit" className="auth-btn" disabled={submitting}>
                    {submitting ? "Creating account..." : "Create account"}
                  </button>
                )}
              </div>
            </form>
          )}

          <footer className="auth-foot">
            © {new Date().getFullYear()} Nnetego. All rights reserved. <Link to="/legal/privacy">Privacy</Link> · <Link to="/legal/terms">Terms</Link>
          </footer>
        </div>
      </main>
    </div>
  );
}
